/* =========================================================================
 * Researchers landing — /researchers
 *
 * The B2B-flavored entry point for the brief's "Consumer / Research Portal".
 * It's purely a marketing landing — actual data inquiries go through
 * /consumer (the existing inquiry flow).
 *
 * Sections:
 *   1. Hero — "Access community-powered environmental data"
 *   2. Dataset categories grid — 6 tiles
 *   3. Why our data — 3 differentiators (coverage / freshness / methodology)
 *   4. CTA strip — "Talk to us about your project"
 * ========================================================================= */

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import SiteFooter from "components/SiteFooter";

import consumerRoutes from "consumerRoutes";
import footerRoutes from "footer.routes";

import { InquiryForm } from "pages/Researchers/components/InquiryForm";
// Reused from the homepage — same component, so the nav's `#download` anchor
// finds an `id="download"` section here too.
import { Download } from "pages/presentation/components/Download";
import { SampleData } from "pages/Researchers/components/SampleData";
import { DataExplorer } from "pages/Researchers/components/DataExplorer";

// Datasets we actually collect, as produced by the Android app's sensor
// collectors. No air quality, temperature, or humidity — modern phones don't
// have those sensors. Reframe accordingly.
const DATASETS = [
  {
    icon: "speed",
    title: "Atmospheric pressure",
    body: "Barometric pressure series (1 Hz), useful for storm tracking, altitude estimation, and microclimate studies.",
  },
  {
    icon: "directions_walk",
    title: "Motion patterns",
    body: "Accelerometer, gyroscope, magnetometer, device orientation, and compass — for transport-mode and activity research.",
  },
  {
    icon: "light_mode",
    title: "Ambient light",
    body: "Illuminance in lux. Useful for studying urban lighting, day/night cycles, and indoor/outdoor transitions.",
  },
  {
    icon: "graphic_eq",
    title: "Ambient sound",
    body: "Sound amplitude only — no recordings. Useful for noise-pollution maps and urban-soundscape research.",
  },
  {
    icon: "public",
    title: "Geographic coverage",
    body: "Location traces with speed, bearing, and altitude. Aggregated into 250 km contribution zones for privacy.",
  },
  {
    icon: "signal_cellular_alt",
    title: "Cellular signals",
    body: "Carrier name, network type, and signal strength — for connectivity studies and rural-coverage research.",
  },
];

const DIFFERENTIATORS = [
  {
    eyebrow: "Coverage",
    title: "Spatial gaps filled by people",
    body: "Areas without ground-station infrastructure benefit most. Devices in pockets and bags contribute where dedicated hardware doesn't exist.",
  },
  {
    eyebrow: "Freshness",
    title: "Real-time, not annual",
    body: "Readings stream in continuously. Datasets reflect today, not the previous fiscal year.",
  },
  {
    eyebrow: "Methodology",
    title: "Documented, versioned, auditable",
    body: "Every aggregation rule is published. Reproducible from raw readings to delivered dataset.",
  },
];

function Researchers() {
  return (
    <>
      <DefaultNavbar routes={consumerRoutes} sticky />

      {/* Hero — dark navy to differentiate from the homepage's blue gradient
          and signal the B2B/research positioning. The accent phrase uses the
          sky-blue secondary instead of primary blue (better contrast on dark).
          A subtle radial highlight + faint dotted grid keep it from feeling
          flat. */}
      <MKBox
        component="section"
        sx={({ palette }) => ({
          position: "relative",
          minHeight: { xs: "60vh", md: "65vh" },
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
          background: `radial-gradient(ellipse at top, rgba(37, 99, 255, 0.25) 0%, transparent 55%), linear-gradient(180deg, #0B1220 0%, ${palette.dark.main} 100%)`,
          pt: { xs: 12, md: 16 },
          pb: { xs: 10, md: 12 },
          // Faint dotted-grid texture
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: "radial-gradient(rgba(148, 163, 184, 0.12) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            pointerEvents: "none",
            maskImage:
              "linear-gradient(180deg, transparent 0%, black 30%, black 70%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(180deg, transparent 0%, black 30%, black 70%, transparent 100%)",
          },
        })}
      >
        <Container sx={{ position: "relative", zIndex: 1 }}>
          <Grid container justifyContent="center" textAlign="center">
            <Grid item xs={12} md={10} lg={9}>
              <MKTypography
                variant="overline"
                fontWeight="bold"
                sx={({ palette }) => ({
                  color: palette.info.main,
                  letterSpacing: "0.1em",
                })}
              >
                Environmental intelligence marketplace
              </MKTypography>
              <MKTypography
                variant="h1"
                mt={1.5}
                sx={({ breakpoints, typography: { size }, palette }) => ({
                  color: palette.white.main,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  [breakpoints.down("md")]: { fontSize: size["3xl"] },
                })}
              >
                Access community-powered{" "}
                <MKBox component="span" sx={{ color: "info.main" }}>
                  environmental data
                </MKBox>
              </MKTypography>
              <MKTypography
                variant="body1"
                mt={3}
                sx={({ palette }) => ({
                  color: palette.grey[400],
                  maxWidth: 720,
                  mx: "auto",
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: 1.55,
                })}
              >
                Aggregated environmental datasets generated by a global contributor network.
                Licensed for research, planning, and public-good applications.
              </MKTypography>
              <MKBox mt={5} display="flex" gap={2} flexWrap="wrap" justifyContent="center">
                <MKButton
                  variant="gradient"
                  color="primary"
                  size="large"
                  href="#inquiry"
                  sx={{ px: 4 }}
                >
                  Send an inquiry
                </MKButton>
                <MKButton
                  variant="outlined"
                  color="white"
                  size="large"
                  href="/contact-sales"
                  sx={{ px: 4 }}
                >
                  Contact sales
                </MKButton>
              </MKBox>
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      {/* Dataset categories */}
      <MKBox component="section" py={{ xs: 8, md: 12 }} px={{ xs: 2, md: 4 }}>
        <Container>
          <Grid container justifyContent="center" textAlign="center" mb={{ xs: 5, md: 7 }}>
            <Grid item xs={12} md={8}>
              <MKTypography
                variant="overline"
                color="primary"
                fontWeight="bold"
                sx={{ letterSpacing: "0.1em" }}
              >
                Datasets
              </MKTypography>
              <MKTypography
                variant="h2"
                color="dark"
                mt={1}
                sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}
              >
                What&apos;s available
              </MKTypography>
              <MKTypography variant="body1" color="text" mt={2} sx={{ maxWidth: 640, mx: "auto" }}>
                Datasets are sliced by category, geography, time window, and contributor activity
                mode. Custom slices on request.
              </MKTypography>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {DATASETS.map((d) => (
              <Grid item xs={12} sm={6} md={4} key={d.title}>
                <Card
                  elevation={0}
                  sx={({ palette, borders }) => ({
                    p: 3,
                    height: "100%",
                    border: `1px solid ${palette.grey[200]}`,
                    borderRadius: borders.borderRadius.lg,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 14px 28px -16px rgba(15, 23, 42, 0.18)",
                    },
                  })}
                >
                  <MKBox
                    sx={({ palette }) => ({
                      width: 44,
                      height: 44,
                      borderRadius: "10px",
                      display: "grid",
                      placeItems: "center",
                      backgroundColor: "rgba(37, 99, 255, 0.1)",
                      color: palette.primary.main,
                      mb: 2,
                    })}
                  >
                    <Icon fontSize="small">{d.icon}</Icon>
                  </MKBox>
                  <MKTypography variant="h6" color="dark" fontWeight="bold" mb={1}>
                    {d.title}
                  </MKTypography>
                  <MKTypography variant="body2" color="text">
                    {d.body}
                  </MKTypography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </MKBox>

      <SampleData />

      <DataExplorer />

      {/* Why our data */}
      <MKBox
        component="section"
        py={{ xs: 8, md: 12 }}
        px={{ xs: 2, md: 4 }}
        sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[200]}` })}
      >
        <Container>
          <Grid container justifyContent="center" textAlign="center" mb={{ xs: 5, md: 7 }}>
            <Grid item xs={12} md={8}>
              <MKTypography
                variant="overline"
                color="primary"
                fontWeight="bold"
                sx={{ letterSpacing: "0.1em" }}
              >
                Why our data
              </MKTypography>
              <MKTypography
                variant="h2"
                color="dark"
                mt={1}
                sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}
              >
                Three things traditional sources can&apos;t give you
              </MKTypography>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {DIFFERENTIATORS.map((d) => (
              <Grid item xs={12} md={4} key={d.title}>
                <MKBox sx={{ p: { xs: 0, md: 1 } }}>
                  <MKTypography
                    variant="overline"
                    color="primary"
                    fontWeight="bold"
                    sx={{ letterSpacing: "0.1em" }}
                  >
                    {d.eyebrow}
                  </MKTypography>
                  <MKTypography variant="h5" color="dark" fontWeight="bold" mt={1} mb={1.5}>
                    {d.title}
                  </MKTypography>
                  <MKTypography variant="body2" color="text" sx={{ lineHeight: 1.6 }}>
                    {d.body}
                  </MKTypography>
                </MKBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </MKBox>

      <InquiryForm />

      <Download />

      <SiteFooter content={footerRoutes} />
    </>
  );
}

export default Researchers;
